import { Injectable, QueryList } from '@angular/core';
import { EpicrisisInput } from '../interfaces/epicrisis-report-field.interface';
import { Epicrisis } from '../interfaces/epicrisis.interface';
import { EpicrisisInputComponent } from '../epicrisis-input/epicrisis-input.component';
import type { TDocumentDefinitions } from 'pdfmake/interfaces';
import { PdfService } from './pdf.service';

@Injectable({
  providedIn: 'root'
})
export class EpicrisisHelperService {

  constructor(private readonly pdsService: PdfService) { }

  //  Sunucuya gönderilecek formata çevirir
  convertToInputs(epicrisis: Epicrisis): EpicrisisInput[] {
    return (Object.keys(epicrisis) as (keyof Epicrisis)[])
      .filter(key => key !== 'patientInformations') 
      .map(key => ({
        isActive: true,
        title: this.getDisplayName(key),
        key: key,
        value: epicrisis[key] as string,
      }));
  }

  //  Sunucudan gelen veri formatını çevirir
  convertToEpicrisis(inputs: EpicrisisInput[], components: QueryList<EpicrisisInputComponent>, patientInformations?: any[]): Epicrisis {
    const epicrisis: Partial<Epicrisis> = {};
    if (patientInformations) {
      epicrisis.patientInformations = patientInformations;
    }
    inputs.forEach(input => {
      if (input.key !== 'patientInformations') { // patientInformations'ı atla
        epicrisis[input.key as keyof Epicrisis] = input.value as any;
        const comp = components.find(c => c.label === input.title);
        if (comp) {
          comp.isActive = input.isActive;
        }
      }
    });
    return epicrisis as Epicrisis;
  }

  private getDisplayName(key: keyof Epicrisis): string {
    const displayNames: Record<keyof Epicrisis, string> = {
      caseDate: 'Vaka Tarihi',
      protocolNo: 'Protokol',
      doctor: 'Hekim',
      patientName: 'Hasta Adı-Soyadı',
      age: 'Yaş',
      gender: 'Cinsiyet',
      diagnoses: 'Ön tanı',
      complaint: 'Şikayet',
      anamnesis: 'Anamnez',
      vitalSigns: 'Vital Bulgular',
      pupils: 'Pupiller',
      skin: 'Cilt',
      isResqued: 'Kurtarılma Yapıldı Mı?',
      respiratory: 'Solunum',
      physicalExamination: 'Fiziki Muayene',
      skinExaminationAnswer: 'Cilt Muayenesi Cevap',
      skinExaminationDescription: 'Cilt Muayenesi Açıklama',
      skinExamination: 'Cilt Muayenesi',
      headExaminationAnswer: 'Baş-boyun Muayenesi Cevap',
      headExaminationDescription: 'Baş-boyun Muayenesi Açıklama',
      headExamination: 'Baş-boyun Muayenesi',
      respiratoryExaminationAnswer: 'Solunum Sistemi Muayenesi Cevap',
      respiratoryExaminationDescription: 'Solunum Sistemi Muayenesi Açıklama',
      respiratoryExamination: 'Solunum Sistemi',
      cardiovascularExaminationAnswer: 'Kardiyovasküler Sistem Muayenesi Cevap',
      cardiovascularExaminationDescription: 'Kardiyovasküler Sistem Muayenesi Açıklama',
      cardiovascularExamination: 'Kardiyovasküler Sistem',
      abdominalExaminationAnswer: 'Batın Muayenesi Cevap',
      abdominalExaminationDescription: 'Batın Muayenesi Açıklama',
      abdominalExamination: 'Batın Muayenesi',
      neurologicalExaminationAnswer: 'Nörolojik Muayenesi Cevap',
      neurologicalExaminationDescription: 'Nörolojik Muayenesi Açıklama',
      neurologicalExamination: 'Nörolojik Muayenesi',
      extremesAnswer: 'Ekstremiteler Cevap',
      extremesDescription: 'Ekstremiteler Açıklama',
      extremes: 'Ekstremiteler',
      treatment: 'Tedavi',
      services: 'Hizmetler',
      motor: 'Glasgow Motor',
      motorScore: 'Glasgow Motor Skor',
      verbal: 'Glasgow Verbal',
      verbalScore: 'Glasgow Verbal Skor',
      eye: 'Glasgow Göz',
      eyeScore: 'Glasgow Göz Skor',
      gksScore: 'GKS Toplam Skor',
      gksText: 'GKS Açıklama',
      patientInformations: 'Hasta Bilgileri'
    };

    return displayNames[key] || key; // Eğer key bulunamazsa, key'i direkt döndür
  }

  getEmptyEpicrisisData(): Epicrisis {
  const emptyEpicrisis: Partial<Epicrisis> = {
    patientInformations: []
  };

  // Tüm string alanları boş string yap
  const stringKeys: (keyof Omit<Epicrisis, 'patientInformations'>)[] = [
    'caseDate', 'protocolNo', 'doctor', 'patientName', 'age', 'gender',
    'diagnoses', 'complaint', 'anamnesis', 'vitalSigns', 'pupils', 'skin',
    'isResqued', 'respiratory', 'physicalExamination', 'skinExaminationAnswer',
    'skinExaminationDescription', 'skinExamination', 'headExaminationAnswer',
    'headExaminationDescription', 'headExamination', 'respiratoryExaminationAnswer',
    'respiratoryExaminationDescription', 'respiratoryExamination',
    'cardiovascularExaminationAnswer', 'cardiovascularExaminationDescription',
    'cardiovascularExamination', 'abdominalExaminationAnswer',
    'abdominalExaminationDescription', 'abdominalExamination',
    'neurologicalExaminationAnswer', 'neurologicalExaminationDescription',
    'neurologicalExamination', 'extremesAnswer', 'extremesDescription',
    'extremes', 'treatment', 'services', 'motor', 'motorScore', 'verbal',
    'verbalScore', 'eye', 'eyeScore', 'gksScore', 'gksText'
  ];

  stringKeys.forEach(key => {
    emptyEpicrisis[key] = '';
  });

  return emptyEpicrisis as Epicrisis;
}

  async generateEpicrisisPdf(
    inputs: EpicrisisInput[],
    approvalDate: string,
    isMail?:boolean,
  ) {
    const content: any[] = [];

    content.push({
      text: `${approvalDate}`,
      alignment: 'right',
      margin: [0, 0, 0, 10],
      style: 'dateText'
    });

    content.push({ text: 'EPİKRİZ RAPORU', style: 'header' });

    const vakaTarihi = inputs.find(f => f.key === 'caseDate' && f.isActive)?.value ?? '';
    const protokol = inputs.find(f => f.key === 'protocolNo' && f.isActive)?.value ?? '';
    const hastaAdi = inputs.find(f => f.key === 'patientName' && f.isActive)?.value ?? '';
    const yas = inputs.find(f => f.key === 'age' && f.isActive)?.value ?? '';
    const cinsiyet = inputs.find(f => f.key === 'gender' && f.isActive)?.value ?? '';
    const hekim = inputs.find(f => f.key === 'doctor' && f.isActive)?.value ?? '';

    content.push({
      table: {
        widths: [120, '*', 100, '*'],
        body: [
          [
            { text: 'Vaka Tarihi :', style: 'label' }, vakaTarihi,
            { text: 'Protokol :', style: 'label' }, protokol
          ],
          [
            { text: 'Hekim :', style: 'label' }, hekim,
            { text: 'Hasta Adı-Soyadı :', style: 'label' }, hastaAdi,
          ],
          [
            { text: 'Yaş :', style: 'label' }, yas,
            { text: 'Cinsiyet :', style: 'label' }, cinsiyet
          ]
        ]
      },
      layout: 'noBorders',
      margin: [0, 10, 0, 20]
    });

    const orderedKeys: string[] = [
      'diagnoses',
      'complaint',
      'anamnesis',
      'vitalSigns',
      'pupils',
      'skin',
      'isResqued',
      'respiratory',
      'physicalExamination',
      'skinExamination',
      'headExamination',
      'respiratoryExamination',
      'cardiovascularExamination',
      'abdominalExamination',
      'neurologicalExamination',
      'gksScore',
      'gksText',
      'extremes',
      'treatment',
      'services'
    ];
    const keyForLine = this.findActiveKey(inputs);

    if(keyForLine === 'none-active') {
      content.push({
        canvas: [
          { type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1 }
        ],
        margin: [0, 5, 0, 5]
      });
    }
    for(const key of orderedKeys){
      const field = inputs.find(f => f.key === key && f.isActive);
      if (field) {
        if(field.key === 'services') {
          content.push({ text: ' ', margin: [0, 5] });
        }
        content.push({
          table: {
            widths: [150, '*'],
            body: [
              [
                { text: field.title + ' :', style: 'label' },
                { text: field.value ?? '-' }
              ]
            ]
          },
          layout: 'noBorders',
          margin: [0, 2, 0, 2]
        });

        // Anamnez sonrası çizgi
        if (field.key === keyForLine) {
          content.push({
            canvas: [
              { type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1 }
            ],
            margin: [0, 5, 0, 5]
          });
        }
      }
    }

    // Footer
    content.push({
      stack: [
        '',
        '',
        'ACIBADEM MOBİL SAĞLIK HİZMETLERİ A.Ş.',
        'Acıbadem Genel Müdürlük, Atatürk Mah. Feza Sk. No: 3/5 Ataşehir/İstanbul',
        '+90 216 445 44 44   F +90 216 445 44 51',
        'MOBİL SAĞLIK HATTI  www.acibademmobil.com.tr  444 9 724'
      ],
      style: 'footer',
      margin: [0, 40, 0, 0]
    });

    const docDefinition: TDocumentDefinitions = {
      content,
      styles: {
        header: { fontSize: 14, bold: true, alignment: 'center', margin: [0, 0, 0, 10] },
        label: { bold: true, fontSize: 9, },
        dateText: { bold: false, fontSize: 9, },
        footer: { fontSize: 8, alignment: 'center' }
      },
      defaultStyle: {
        font: 'Roboto',
        fontSize: 9,
        color: '#333333'
      }
    };

    if (isMail) {
    return await this.pdsService.generatePdfBase64(docDefinition);
  } else {
    await this.pdsService.generatePdf(docDefinition);
    return;
  }
  }

  private findActiveKey(inputs: EpicrisisInput[]): string {
    const activeInputs = inputs.filter(f => f.isActive && ['diagnoses', 'complaint', 'anamnesis'].includes(f.key));
    if(activeInputs.some(f => f.key === 'anamnesis')) {
      return 'anamnesis';
    } else if(activeInputs.some(f => f.key === 'complaint')) {
      return 'complaint';
    } else if(activeInputs.some(f => f.key === 'diagnoses')) {
      return 'diagnoses';
    } else {
      return 'none-active';
    }
  }

}
